import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Archive } from 'lucide-react';
import WikiIcon, { WikiIconSquare } from '../shared/WikiIcon';
import useWikiRole from '../shared/useWikiRole';
import { useWikiCategories, useWikiCounts, useWikiPage } from '../shared/useWikiQueries';
import { useWikiUi, wikiUi } from '../shared/wikiUi';
import { parseWikiPath, wikiPaths } from '../shared/wikiRoutes';
import { categoryColors } from '../shared/wikiFormat';
import { navBadgeText, wikiNavActive } from './wikiNavModel';
import { useBrainTreeState } from '../brain/tree/brainTreeState';
import '../shared/wiki.css';
import './wikiNav.css';

/**
 * The Wiki's contextual nav (spec A §1.3), rendered by shell/ContextualSidebar
 * for the Wiki workspace's `render: 'wiki-nav'` section, inside the shell's
 * own sidebar (`nav.sidebar-scroll`, under its "Wiki" header).
 *
 * Structure + content = the prototype: Home / Ask the Wiki / To acknowledge
 * (teal count) → CATEGORIES label with "+" → category rows (chevron, coloured
 * icon square, name, page count) with their expanded subcategories and "Add
 * subcategory" → MANAGE › AI Brain (amber pending count).
 * Type + spacing = the shell sidebar classes (REBUILD-PLAN §1), so the Wiki
 * nav reads exactly like the CRM nav; selection uses the shell's `.active`.
 *
 * Roles (REBUILD-PLAN §1): "+" and "Add subcategory" for Editors+ (canEdit);
 * MANAGE › AI Brain for Brain viewers (admin + editor); its amber count for
 * Wiki admins only. Expansion lives in `wikiUi.navOpen`, so it survives route
 * changes and sidebar remounts; the first category starts expanded and the
 * category holding the selected subcategory opens itself.
 *
 * Call `onNavigate()` after every navigation or modal open (it closes the
 * mobile off-canvas sidebar).
 */

// The other sidebar badges refresh every 60 s (ContextualSidebar); match it.
const NAV_COUNTS_REFRESH_MS = 60 * 1000;

const TOP_ROWS = Object.freeze([
    Object.freeze({ id: 'home', label: 'Home', icon: 'house', to: wikiPaths.home() }),
    Object.freeze({ id: 'ask', label: 'Ask the Wiki', icon: 'sparkles', to: wikiPaths.ask() }),
    Object.freeze({ id: 'ack', label: 'To acknowledge', icon: 'badge-check', to: wikiPaths.ack() }),
]);

function NavRow({ id, label, icon, to, on, count, tone, onNavigate }) {
    const badge = navBadgeText(count);
    return (
        <Link
            to={to}
            onClick={onNavigate}
            data-testid={`wiki-nav-${id}`}
            className={`sidebar-item${on ? ' active' : ''}`}
            aria-current={on ? 'page' : undefined}
        >
            <span className="sidebar-item-icon"><WikiIcon name={icon} size={18} /></span>
            <span className="sidebar-label">{label}</span>
            {badge && (
                <span
                    className={`sidebar-badge wk-nav-badge wk-nav-badge--${tone}`}
                    data-testid={`wiki-nav-${id}-count`}
                >
                    {badge}
                </span>
            )}
        </Link>
    );
}

function CategoryRow({ category, open, active, canEdit, onNavigate }) {
    const { color, tint } = categoryColors(category);
    const selected = active.categoryId === category.id;
    const subsId = `wk-nav-subs-${category.id}`;
    const subs = category.subs || [];

    const select = () => {
        // Prototype `select`: expand, then open the category.
        wikiUi.setNavOpen(category.id, true);
        if (onNavigate) onNavigate();
    };
    const addSub = () => {
        wikiUi.openModal('subcategory', { parentId: category.id });
        if (onNavigate) onNavigate();
    };

    return (
        <div className="wk-nav-cat" data-testid={`wiki-nav-cat-${category.id}`}>
            <div className="wk-nav-cat-row">
                <button
                    type="button"
                    className="wk-nav-chevron"
                    aria-expanded={open}
                    aria-controls={open ? subsId : undefined}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${category.name || 'category'}`}
                    data-testid={`wiki-nav-toggle-${category.id}`}
                    onClick={() => wikiUi.toggleNavOpen(category.id)}
                >
                    <WikiIcon name={open ? 'chevron-down' : 'chevron-right'} size={13} />
                </button>
                <Link
                    to={wikiPaths.category(category.id)}
                    onClick={select}
                    className={`sidebar-item wk-nav-cat-link${selected ? ' active' : ''}`}
                    aria-current={selected ? 'page' : undefined}
                    data-testid={`wiki-nav-cat-link-${category.id}`}
                >
                    <WikiIconSquare icon={category.icon} color={color} tint={tint} size={22} />
                    <span className="sidebar-label">{category.name}</span>
                    <span className="wk-nav-num" data-testid={`wiki-nav-cat-count-${category.id}`}>
                        {Number(category.page_count) || 0}
                    </span>
                </Link>
            </div>
            {open && (
                <div className="wk-nav-subs" id={subsId}>
                    {subs.map((sub) => {
                        const on = active.subId === sub.id;
                        return (
                            <Link
                                key={sub.id}
                                to={wikiPaths.category(sub.id)}
                                onClick={onNavigate}
                                className={`sidebar-item wk-nav-sub${on ? ' active' : ''}`}
                                aria-current={on ? 'page' : undefined}
                                data-testid={`wiki-nav-sub-${sub.id}`}
                            >
                                <span className="sidebar-label">{sub.name}</span>
                                <span className="wk-nav-num">{Number(sub.page_count) || 0}</span>
                            </Link>
                        );
                    })}
                    {canEdit && (
                        <button
                            type="button"
                            className="sidebar-item wk-nav-add-sub"
                            onClick={addSub}
                            data-testid={`wiki-nav-add-sub-${category.id}`}
                        >
                            <WikiIcon name="plus" size={13} />
                            <span className="sidebar-label">Add subcategory</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default function WikiNav({ onNavigate }) {
    const location = useLocation();
    const role = useWikiRole();
    const navOpen = useWikiUi((state) => state.navOpen);
    const route = parseWikiPath(location.pathname, location.search);
    const pageId = route?.view === 'page' ? route.id : null;

    const categories = useWikiCategories({ enabled: role.canRead });
    const counts = useWikiCounts({ enabled: role.canRead, refetchInterval: NAV_COUNTS_REFRESH_MS });
    // Cache-only read: the page view loads ['wiki','page',id]; the nav needs its
    // subcategory to keep that row selected, and must not fetch a body itself.
    const cachedPage = useWikiPage(pageId, { enabled: false });

    const active = wikiNavActive(route, { page: cachedPage.data, index: categories });
    const firstId = categories.tree[0]?.id || null;
    const expandId = active.expandId;
    // Prototype: re-entering the Brain from the nav restores the last Brain tab.
    const lastBrainTab = useBrainTreeState((s) => s.lastTab);

    // Prototype initial state `wkOpen: { <first category>: true }` — seeded once
    // per session (an explicit collapse is remembered in wikiUi.navOpen).
    useEffect(() => {
        if (firstId && Object.keys(wikiUi.getState().navOpen).length === 0) wikiUi.setNavOpen(firstId, true);
    }, [firstId]);

    // Auto-expand the category that holds the selected subcategory (after the
    // seed above, so a deep link never suppresses the first-category default).
    useEffect(() => {
        if (firstId && expandId) wikiUi.setNavOpen(expandId, true);
    }, [firstId, expandId]);

    const ackCount = counts.data?.acknowledge;
    const brainCount = role.canAdmin ? counts.data?.brain_pending : null;
    const loaded = categories.isSuccess;

    const openNewCategory = () => {
        wikiUi.openModal('category', {});
        if (onNavigate) onNavigate();
    };

    return (
        <div className="wk-vars wk-nav" data-testid="wiki-nav">
            <div className="sidebar-section">
                {TOP_ROWS.map((row) => (
                    <NavRow
                        key={row.id}
                        {...row}
                        on={active.top === row.id}
                        count={row.id === 'ack' ? ackCount : null}
                        tone="teal"
                        onNavigate={onNavigate}
                    />
                ))}
            </div>

            {role.canRead && (
                <div className="sidebar-section" data-testid="wiki-nav-categories">
                    <div className="sidebar-section-label wk-nav-label">
                        <span className="wk-nav-label-text">Categories</span>
                        {role.canEdit && (
                            <button
                                type="button"
                                className="wk-nav-plus"
                                title="New category"
                                aria-label="New category"
                                data-testid="wiki-nav-new-category"
                                onClick={openNewCategory}
                            >
                                <WikiIcon name="plus" size={15} />
                            </button>
                        )}
                    </div>
                    {categories.isLoading && (
                        <div className="wk-nav-note" role="status">Loading categories…</div>
                    )}
                    {categories.isError && (
                        <div className="wk-nav-note wk-nav-note--error" role="alert">
                            Categories didn&apos;t load.{' '}
                            <button type="button" className="wk-nav-retry" onClick={() => categories.refetch()}>
                                Try again
                            </button>
                        </div>
                    )}
                    {loaded && categories.tree.length === 0 && (
                        <div className="wk-nav-note">No categories yet.</div>
                    )}
                    {categories.tree.map((category) => (
                        <CategoryRow
                            key={category.id}
                            category={category}
                            open={Boolean(navOpen[category.id])}
                            active={active}
                            canEdit={role.canEdit}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            )}

            {(role.canBrainView || role.canEdit) && (
                <div className="sidebar-section" data-testid="wiki-nav-manage">
                    <div className="sidebar-section-label">Manage</div>
                    {role.canBrainView && (
                        <NavRow
                            id="brain"
                            label="AI Brain"
                            icon="brain"
                            to={wikiPaths.brain(lastBrainTab)}
                            on={active.top === 'brain'}
                            count={brainCount}
                            tone="amber"
                            onNavigate={onNavigate}
                        />
                    )}
                    {/* #872: editors restore pages, Wiki admins also categories. */}
                    {role.canEdit && (
                        <NavRow
                            id="archived"
                            label="Archived"
                            icon={Archive}
                            to={wikiPaths.archived()}
                            on={active.top === 'archived'}
                            onNavigate={onNavigate}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
